import React, { useState } from "react";
import { LEARN } from "../theme";
import WritingSketchCanvas from "./WritingSketchCanvas";
import { CheckIcon, ClearIcon, IconButton, StickerAction, UndoIcon, paperSurface } from "./games/shared";

/**
 * Free writing paper: any ink plus Undo / Clear / Done. Used where iOS does
 * not run geometry scoring (sentences, story lines, drawings). Fills the
 * height it is given; kindergarten lines by default.
 */
const WritingFreePad = ({
  accent = LEARN.color.lavender,
  prompt = "",
  doneTitle = "Done",
  onDone,
  resetKey = 0,
  sfx,
  reduceMotion = false,
  lines = true,
  minHeight = 150,
  disabled = false,
  style,
}) => {
  const [strokes, setStrokes] = useState([]);
  const hasInk = strokes.some((stroke) => (stroke.points || []).length >= 2);

  const undo = () => {
    if (!strokes.length) return;
    sfx?.("pop", { volume: 0.4 });
    setStrokes((prev) => prev.slice(0, -1));
  };
  const clear = () => {
    if (!strokes.length) return;
    sfx?.("whoosh", { volume: 0.4 });
    setStrokes([]);
  };
  const done = () => {
    if (!hasInk) return;
    sfx?.("correct", { volume: 0.6 });
    onDone?.(strokes);
    setStrokes([]);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1, minHeight: 0, minWidth: 0, fontFamily: LEARN.font, ...style }}>
      <div
        style={{
          position: "relative",
          flex: 1,
          minHeight,
          ...paperSurface(),
          overflow: "hidden",
        }}
      >
        <WritingSketchCanvas
          key={resetKey}
          strokes={strokes}
          onChange={setStrokes}
          accent={accent}
          showGuide={false}
          paper={lines}
          disabled={disabled}
        />
        {!disabled && (
          <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 8 }}>
            <IconButton label="Undo last stroke" onClick={undo} disabled={!strokes.length} size={40} frosted accent={LEARN.color.ink}>
              <UndoIcon size={18} />
            </IconButton>
            <IconButton label="Clear the paper" onClick={clear} disabled={!strokes.length} size={40} frosted accent={LEARN.color.ink}>
              <ClearIcon size={18} />
            </IconButton>
          </div>
        )}
        {prompt && (
          <div
            style={{
              position: "absolute",
              left: 12,
              bottom: 10,
              height: 30,
              padding: "0 12px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.86)",
              border: "1.5px solid rgba(11,26,51,0.10)",
              fontWeight: 700,
              fontSize: 13,
              color: LEARN.color.muted,
              display: "flex",
              alignItems: "center",
              pointerEvents: "none",
              maxWidth: "calc(100% - 24px)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {prompt}
          </div>
        )}
      </div>
      {!disabled && (
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexShrink: 0 }}>
          <StickerAction onClick={done} accent={accent} disabled={!hasInk} size="lg" icon={<CheckIcon size={22} />} ariaLabel={`${doneTitle} writing`} style={{ minWidth: 200, minHeight: 58 }}>
            {doneTitle}
          </StickerAction>
        </div>
      )}
    </div>
  );
};

export default WritingFreePad;
